import { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { API } from "../config";
import { PersonaHeader } from "../components/persona/PersonaSections";
import { ProposalPreviews } from "../components/webdesign/ProposalPreviews";
import { ReviewForm } from "../components/webdesign/ReviewForm";
import { ReferralBox } from "../components/webdesign/ReferralBox";

const fmtPrice = (v) => (v % 1 ? `€${v.toFixed(2).replace(".", ",")}` : `€${v}`);

const T = {
  sk: { title: "Váš návrh webu", hi: "Dobrý deň", lead: "Návrh je schválený. Po zaplatení {price} web spustíme do 14 dní a zapneme vám 3 mesiace plánu Pro zadarmo.",
    view: "Pozrieť návrh", pay: "Zaplatiť {price} kartou", paying: "Presmerúvam na platbu…", paid: "Zaplatené. Plán Pro máte aktívny do", checking: "Overujem platbu…",
    err: "Platbu sa nepodarilo spustiť. Skúste znova.", stageTitle: "Stav vášho webu", launch: "Plánované spustenie", launched: "Spustené", proTitle: "Pokračujte v pláne Pro", proText: "Pro zadarmo vám končí o {d} dní. Pokračujte za €49/mes., platiť začnete až po skončení bezplatného obdobia. Zrušiť sa dá kedykoľvek.", proBtn: "Pokračovať v Pro za €49/mes.", proDone: "Ďakujeme! Plán Pro pokračuje.", missing: "Návrh sa nenašiel. Skontrolujte odkaz z emailu.", wait: "Návrh ešte nie je schválený.", login: "Prihlásiť sa" },
  cs: { title: "Váš návrh webu", hi: "Dobrý den", lead: "Návrh je schválený. Po zaplacení {price} web spustíme do 14 dnů a zapneme vám 3 měsíce plánu Pro zdarma.",
    view: "Zobrazit návrh", pay: "Zaplatit {price} kartou", paying: "Přesměrovávám na platbu…", paid: "Zaplaceno. Plán Pro máte aktivní do", checking: "Ověřuji platbu…",
    err: "Platbu se nepodařilo spustit. Zkuste to znovu.", stageTitle: "Stav vašeho webu", launch: "Plánované spuštění", launched: "Spuštěno", proTitle: "Pokračujte v plánu Pro", proText: "Pro zdarma vám končí za {d} dní. Pokračujte za €49/měs., platit začnete až po skončení bezplatného období. Zrušit lze kdykoli.", proBtn: "Pokračovat v Pro za €49/měs.", proDone: "Děkujeme! Plán Pro pokračuje.", missing: "Návrh se nenašel. Zkontrolujte odkaz z e-mailu.", wait: "Návrh ještě není schválený.", login: "Přihlásit se" },
};

const fmtD = (iso, lang) => new Date(iso).toLocaleDateString(lang === "cs" ? "cs-CZ" : "sk-SK");

const StageTracker = ({ p, t, lang }) => {
  const last = p.stages.length - 1;
  return (
    <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="proposal-stages">
      <h2 className="text-xl font-black">{t.stageTitle}</h2>
      <p className="mt-1 text-sm text-white/60" data-testid="proposal-launch-date">{p.stage === last ? t.launched : t.launch}: {fmtD(p.launch_date, lang)}</p>
      <ol className="mt-6 space-y-4">{p.stages.map((s, i) => {
        const done = i <= p.stage;
        const day = new Date(new Date(p.paid_at).getTime() + p.stage_days[i] * 864e5).toISOString();
        return (
          <li key={s} className="flex items-center gap-4" data-testid={`proposal-stage-${i}`} data-done={done}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-black transition-colors ${done ? "bg-[#a3e635] text-black" : "bg-white/10 text-white/40"} ${i === p.stage ? "ring-4 ring-[#a3e635]/30" : ""}`}>{done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</span>
            <span className={`flex-1 ${done ? "text-white" : "text-white/45"} ${i === p.stage ? "font-black" : ""}`}>{s}</span>
            <span className="text-xs text-white/45">{fmtD(day, lang)}</span>
          </li>);
      })}</ol>
    </section>
  );
};

const ProOffer = ({ token, p, t }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const go = async () => {
    setBusy(true); setErr(false);
    try { const r = await axios.post(`${API}/web-design/proposal/${token}/pro-checkout`, { origin_url: window.location.origin }); window.location.href = r.data.url; }
    catch { setBusy(false); setErr(true); }
  };
  return (
    <section id="pro" className="mt-8 rounded-2xl border border-[#a3e635]/40 bg-[#a3e635]/[0.06] p-6 scroll-mt-24" data-testid="proposal-pro-offer">
      <h2 className="text-xl font-black">{t.proTitle}</h2>
      <p className="mt-2 text-white/75">{t.proText.replace("{d}", Math.max(p.pro_days_left, 0))}</p>
      <button onClick={go} disabled={busy} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#a3e635] text-black font-black hover:bg-[#bef264] transition-colors disabled:opacity-60" data-testid="proposal-pro-btn">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}{t.proBtn}
      </button>
      {err && <p className="mt-3 text-sm text-red-300" data-testid="proposal-pro-error">{t.err}</p>}
    </section>
  );
};

const PayButton = ({ token, t }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const pay = async () => {
    setBusy(true); setErr(false);
    try {
      const r = await axios.post(`${API}/web-design/proposal/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch { setBusy(false); setErr(true); }
  };
  return (
    <div>
      <button onClick={pay} disabled={busy} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#a3e635] text-black font-black hover:bg-[#bef264] transition-colors disabled:opacity-60" data-testid="proposal-pay-btn">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}{busy ? t.paying : t.pay}
      </button>
      {err && <p className="mt-3 text-sm text-red-300" data-testid="proposal-pay-error">{t.err}</p>}
    </div>
  );
};

export default function WebProposalPage() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [p, setP] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    const load = () => axios.get(`${API}/web-design/proposal/${token}`).then((r) => { setP(r.data); setState("ok"); }).catch(() => setState("missing"));
    const sid = sp.get("session_id");
    if (sid) { setState("checking"); axios.get(`${API}/payments/status/${sid}`).catch(() => {}).finally(load); } else load();
  }, [token, sp]);
  const lang = p?.lang === "cs" ? "cs" : "sk";
  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (p && ["pro", "recenzia"].includes(h)) setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [p]);
  const priceL = fmtPrice(p?.price_eur ?? 499);
  const t = Object.fromEntries(Object.entries(T[lang]).map(([k, v]) => [k, v.replace("{price}", priceL)]));
  const launched = p?.stages && p.stage === p.stages.length - 1;
  const until = p?.plan_expires_at ? new Date(p.plan_expires_at).toLocaleDateString(lang === "cs" ? "cs-CZ" : "sk-SK") : "";
  return (
    <div className="min-h-screen bg-[#07090d] text-white" data-testid="web-proposal-page">
      <Helmet htmlAttributes={{ lang }}><title>{`${t.title} | TopRankPulse`}</title><meta name="robots" content="noindex" /></Helmet>
      <PersonaHeader ctaLabel={lang === "cs" ? "Poptávka" : "Dopyt"} />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        {(state === "loading" || state === "checking") && <p className="flex items-center gap-2 text-white/70" data-testid="proposal-loading"><Loader2 className="w-5 h-5 animate-spin" />{state === "checking" ? t.checking : ""}</p>}
        {state === "missing" && <p className="text-white/70" data-testid="proposal-missing">{t.missing}</p>}
        {state === "ok" && (
          <>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="proposal-title">{t.title}</h1>
            <p className="mt-6 text-lg text-white/80">{t.hi} {p.name}{p.company ? ` (${p.company})` : ""},</p>
            {!p.paid && <p className="mt-2 text-white/75">{t.lead}</p>}
            {!p.paid && p.referred && <p className="mt-3 inline-block rounded-full bg-[#a3e635]/15 text-[#d9f99d] px-3 py-1 text-sm font-bold" data-testid="proposal-ref-discount">{lang === "cs" ? "Sleva 10 % díky doporučení" : "Zľava 10 % vďaka odporúčaniu"}</p>}
            {p.note && <p className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-white/80 whitespace-pre-line" data-testid="proposal-note">{p.note}</p>}
            {p.proposal_url && <a href={p.proposal_url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1 font-bold text-[#a3e635] hover:underline" data-testid="proposal-view-link">{t.view}<ExternalLink className="w-4 h-4" /></a>}
            <div className="mt-10">
              {p.paid ? (
                <div className="rounded-2xl border border-[#a3e635]/40 bg-[#a3e635]/10 p-6 text-[#d9f99d]" data-testid="proposal-paid">
                  <p className="flex gap-2" data-testid={p.pro_subscribed || sp.get("pro") ? "proposal-pro-done" : "proposal-paid-text"}><CheckCircle2 className="w-5 h-5 shrink-0" />{p.pro_subscribed || sp.get("pro") ? t.proDone : `${t.paid} ${until}.`}</p>
                  <a href="/login" className="mt-4 inline-block font-bold underline" data-testid="proposal-login-link">{t.login}</a>
                </div>
              ) : p.status === "approved" ? <PayButton token={token} t={t} /> : <p className="text-white/60" data-testid="proposal-not-approved">{t.wait}</p>}
            </div>
            {p.paid && p.stages && <StageTracker p={p} t={t} lang={lang} />}
            <ProposalPreviews token={token} items={p.previews} lang={lang} />
            {p.pro_offer && !sp.get("pro") && <ProOffer token={token} p={p} t={t} />}
            {launched && <ReviewForm token={token} lang={lang} status={p.review_status} defaultName={p.name} />}
            {p.paid && p.ref_code && <ReferralBox code={p.ref_code} lang={lang} count={p.referrals_paid} />}
          </>
        )}
      </main>
    </div>
  );
}
